import * as React from 'react'
import { useEffect, useRef, useState } from 'react'

import RefreshIcon from '@bufferapp/ui/Icon/Icons/Refresh'
import * as DropdownMenu from '@radix-ui/react-dropdown-menu'
import clsx from 'clsx'

import { IconButton, Input, ScrollArea, Tooltip } from '@buffer-mono/popcorn'
import { BufferTrackerReact as BufferTracker } from '@buffer-mono/tracking-plan'

import {
  getCurrentPage,
  getSegmentChannelMetadata,
} from '~publish/legacy/composer/composer/utils/TrackingUtils'

import BoardCreator from './BoardCreator'
import AppActionCreators from '../action-creators/AppActionCreators'
import type { ComposerProfile, Subprofile } from '../stores/types'
import type { Notification } from '../values/Notification'
import BoardSelectorBoardItem from './boards/BoardSelectorBoardItem/BoardSelectorBoardItem'

import styles from './css/BoardSelector.module.css'

const onClick = (e: React.MouseEvent): void => {
  e.stopPropagation()
}

interface BoardSelectorProps {
  profile: ComposerProfile
  subprofiles: Subprofile[]
  canUnselectSubprofiles?: boolean
  onChange?: () => void
  visibleNotifications: Notification[]
  subprofilesCount?: number
  organizationId: string
  isDropdownItem?: boolean
  needsTriangle?: boolean
}

const BoardSelector = ({
  profile,
  subprofiles,
  canUnselectSubprofiles = true,
  onChange = (): void => {},
  visibleNotifications,
  subprofilesCount = 0,
  organizationId,
  isDropdownItem = false,
  needsTriangle = false,
}: BoardSelectorProps): JSX.Element => {
  const subprofilesContainer = useRef<HTMLDivElement>(null)
  const [searchQuery, setSearchQuery] = useState('')
  const [refreshing, setRefreshing] = useState(false)

  const shouldScrollToBottom =
    subprofilesCount && !profile.subprofilesOrignatedFromAPI
  useEffect(() => {
    if (subprofilesContainer.current) {
      subprofilesContainer.current.scrollTop =
        subprofilesContainer.current.scrollHeight
    }
  }, [shouldScrollToBottom])

  const onSearchChange = (e: React.ChangeEvent<HTMLInputElement>): void => {
    setSearchQuery(e.target.value)
  }

  const refreshBoards = async (): Promise<void> => {
    setRefreshing(true)
    await AppActionCreators.refreshSubprofileData(profile.id)
    setRefreshing(false)

    BufferTracker.refreshPinterestBoardsButtonClicked({
      clientName: 'publishWeb',
      product: 'publish',
      channelId: profile.id,
      organizationId,
    })
  }

  const trackChannelSelected = (): void => {
    const metadata = getSegmentChannelMetadata({
      profile,
      organizationId,
      page: getCurrentPage(),
    })
    BufferTracker.channelSelected(metadata)
  }

  const searchQueryLowerCase = searchQuery.toLowerCase()
  const boards = subprofiles.filter(
    (board) =>
      board.name && board.name.toLowerCase().includes(searchQueryLowerCase),
  )
  // sort board in case-insensitive order
  boards.sort((board, nextBoard) =>
    board.name.toLowerCase().localeCompare(nextBoard.name.toLowerCase()),
  )

  const searchBoardIconClassName = [
    styles.searchBoardIcon,
    'bi bi-search',
  ].join(' ')

  const loadingIconClassName = [
    styles.searchBoardIcon,
    styles.loadingIcon,
    styles.rotate,
    'bi bi-refresh',
  ].join(' ')

  const hasBoardsDisplayed = boards.length > 0
  const profileHasBoards = subprofiles.length > 0

  return (
    // eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions
    <div className={styles.boardSelector} onClick={onClick}>
      {needsTriangle && <div className={styles.boardTriangle}></div>}

      <div className={styles.searchBoardContainer}>
        <div className={styles.boardSearchContainer}>
          {refreshing ? (
            <i className={loadingIconClassName} />
          ) : (
            <i className={searchBoardIconClassName} />
          )}
          <Input
            type="text"
            placeholder="Search your boards"
            value={searchQuery}
            onChange={onSearchChange}
          />
        </div>
        <Tooltip
          content={refreshing ? 'Refreshing boards...' : 'Refresh boards'}
          side="top"
        >
          <IconButton
            label="Refresh boards"
            disabled={refreshing}
            variant="tertiary"
            onClick={refreshBoards}
          >
            <RefreshIcon
              size="medium"
              className={clsx(styles.refreshIconStyled, {
                [styles.disabled]: refreshing,
              })}
            />
          </IconButton>
        </Tooltip>
      </div>
      <ScrollArea
        className={styles.subprofilesContainer}
        ref={subprofilesContainer}
      >
        {[...boards].map((board) => (
          <React.Fragment key={board.id}>
            {!isDropdownItem && (
              <BoardSelectorBoardItem
                profile={profile}
                board={board}
                onChange={onChange}
                canUnselectSubprofiles={canUnselectSubprofiles}
                trackChannelSelected={trackChannelSelected}
              />
            )}
            {/* Below is needed for the dropdown to work after we implemented */}
            {/* scrollbar in the composer. When we decide in the future that */}
            {/* Composer should no longer be in the modal, we can remove */}
            {/* isDropdownItem prop and below conditional */}
            {isDropdownItem && (
              <DropdownMenu.Item>
                <BoardSelectorBoardItem
                  profile={profile}
                  board={board}
                  onChange={onChange}
                  canUnselectSubprofiles={canUnselectSubprofiles}
                  trackChannelSelected={trackChannelSelected}
                />
              </DropdownMenu.Item>
            )}
          </React.Fragment>
        ))}
      </ScrollArea>

      {!hasBoardsDisplayed && (
        <div className={styles.emptySearchResults}>
          {profileHasBoards
            ? 'Sorry, no boards matched your search!'
            : `It looks like you don't have any boards associated with this profile. You can create your first board below.`}
        </div>
      )}

      <BoardCreator
        profile={profile}
        visibleNotifications={visibleNotifications}
      />
    </div>
  )
}

export default BoardSelector
